import { useMemo } from 'react';

import type { PatternDesignOptimisation } from '~api/services';
import { ColourLegend, SideBar } from '~components';
import type { ColourByParameter } from '~types';
import { createContinuousScale } from '~utils';
import { PARAMETERS_LABEL_COLOURS } from '../config';
import { useDrillHoleData } from '../hooks';

import { OptimiserColourByParameter, OptimiserInfo, OptimiserInstructions, OptimiserSelector } from '.';

type OptimiserSidebarProps = {
  /** List of all available optimisations for the blast */
  allOptimisations: PatternDesignOptimisation[];
  /** ID of the currently selected optimisation */
  selectedOptimisationId: number;
  /** Currently selected colour parameter */
  selectedColourByParameter: ColourByParameter;
  /** Callback when a different optimisation is selected */
  onSelectedOptimisationChange: (optimisationId: number) => void;
  /** Callback when colour parameter changes */
  onColourParameterChange: (property: ColourByParameter) => void;
};

/**
 * Displays optimisation details in a sidebar and allows selection of different optimisations
 */
const OptimiserSidebar = ({
  allOptimisations,
  selectedOptimisationId,
  selectedColourByParameter,
  onSelectedOptimisationChange,
  onColourParameterChange,
}: OptimiserSidebarProps) => {
  // Derive selected optimisation from the list
  const selectedOptimisation = allOptimisations.find((opt) => opt.optimisation_id === selectedOptimisationId);

  const drillHoles = useDrillHoleData(selectedOptimisation ?? null);

  // Derive colour scale from props and drill hole data, memoizing for performance.
  // The scale will update whenever the selected optimisation or colour parameter changes.
  const colourScale = useMemo(() => {
    const baseColour = PARAMETERS_LABEL_COLOURS.find((p) => p.value === selectedColourByParameter)?.colour;
    return createContinuousScale(drillHoles, selectedColourByParameter, { baseColour });
  }, [drillHoles, selectedColourByParameter]);

  if (!selectedOptimisation) {
    return null; // Parent should handle this case
  }

  return (
    <SideBar className="w-(--optimiser-sidebar-width) pt-6">
      <OptimiserSelector
        selectedOptimisationId={selectedOptimisationId}
        options={allOptimisations}
        onChange={onSelectedOptimisationChange}
      />
      <OptimiserInfo className="mt-4" selectedOptimisation={selectedOptimisation} />
      <div className="space-y-4">
        <OptimiserColourByParameter
          selectedColourByParameter={selectedColourByParameter}
          onColourByParameterChange={onColourParameterChange}
        />
        <ColourLegend colourScale={colourScale} />
      </div>
      <OptimiserInstructions />
    </SideBar>
  );
};

export { OptimiserSidebar };
